import { FormikProvider, useField, useFormik, useFormikContext } from 'formik';
import { Action, Location } from 'history';
import _ from 'lodash';
import moment from 'moment';
import QuillDelta from 'quill-delta';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router';
import { useHistory } from 'react-router-dom';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import {
	useAuthenticatedUser,
	useCompleteWorkspaceCheckpoint,
	useFeature,
	useFeatureDetails,
	useLocalize,
	useViewer,
} from '@treams/treams-hooks';
import { ArrowLeftIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	CalendarIcon,
	CompassIcon,
	Container,
	DatePicker,
	FlexGap,
	FriendsIcon,
	Heading,
	Heading3,
	Input,
	Label,
	LockIcon,
	Paragraph,
	Popover,
	PropertyChip,
	QuillInput,
	QuillText,
	Select,
	TagIcon,
	Tags,
	Toolbar,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import { ModalContext } from '../navigation/ModalNavigation';
import GoalSuggestionsAI from './goal-ai/GoalSuggestionsAI';
import GoalLearningSuggestions from './goal-learning/GoalLearningSuggestions';
import GoalSelector from './goal-selector/GoalSelector';
import GoalTemplateSelector from './goal-templates/GoalTemplateSelector';
import GoalTemplates from './goal-templates/GoalTemplates';
import SingleGroupSelector from './group-selector/SingleGroupSelector';
import RemainingGoalsStatus from './RemainingGoalsStatus';
import Subgoals from './subgoals/Subgoals';
import SubgoalsContainer from './subgoals/SubgoalsContainer';
import { toChecklistItemInput } from './toChecklistItemInput';

function FeasibilityPicker() {
	const { t } = useTranslation();
	const [{ value }, , { setValue }] = useField<graphql.GoalFeasibility | undefined>('feasibility');
	const [visible, setVisible] = useState(false);

	const options = [
		{
			value: graphql.GoalFeasibility.WillMakeIt,
			title: t('goals.show.feasibility.willMakeIt'),
			color: 'green',
			status: 'success' as const,
		},
		{
			value: graphql.GoalFeasibility.CloseCall,
			title: t('goals.show.feasibility.closeCall'),
			color: 'yellow',
			status: 'warning' as const,
		},
		{
			value: graphql.GoalFeasibility.WontMakeIt,
			title: t('goals.show.feasibility.wontMakeIt'),
			color: 'red',
			status: 'error' as const,
		},
	];

	const selected = options.find((option) => option.value === value);
	const none = t('tags.item.option.none');
	const label = selected ? selected.title : `${t('goals.show.feasibility.label')}: ${none}`;

	return (
		<Popover.Target
			visible={visible}
			onDismiss={() => setVisible(false)}
			contents={
				<Select.Menu
					options={options}
					selectedValue={value}
					onChange={(id) => {
						setValue(id as graphql.GoalFeasibility | undefined);
						setVisible(false);
					}}
					onClose={() => setVisible(false)}
				/>
			}
		>
			<PropertyChip
				status={selected?.status ?? 'off'}
				label={label}
				onClick={() => setVisible(true)}
			/>
		</Popover.Target>
	);
}

function VisibilityPicker() {
	const { t } = useTranslation();
	const [{ value }, , { setValue }] = useField<graphql.GoalVisibility | undefined>('visibility');
	const [visible, setVisible] = useState(false);

	const options = [
		{
			title: t('goals.show.visibility.options.private.label'),
			value: graphql.GoalVisibility.Private,
			context: t('goals.show.visibility.options.private.description'),
			icon: <LockIcon />,
		},
		{
			title: t('goals.show.visibility.options.public.label'),
			value: graphql.GoalVisibility.Public,
			context: t('goals.show.visibility.options.public.description'),
			icon: <FriendsIcon />,
		},
	];

	const selected = options.find((option) => option.value === value);
	const none = t('tags.item.option.none');
	const label = selected ? selected.title : `${t('goals.show.visibility.label')}: ${none}`;

	return (
		<Popover.Target
			visible={visible}
			onDismiss={() => setVisible(false)}
			contents={
				<Select.Menu
					options={options}
					selectedValue={value}
					onChange={(id) => {
						setValue(id as graphql.GoalVisibility);
						setVisible(false);
					}}
					onClose={() => setVisible(false)}
				/>
			}
		>
			<PropertyChip
				icon={value === graphql.GoalVisibility.Public ? <FriendsIcon /> : <LockIcon />}
				label={label}
				onClick={() => setVisible(true)}
			/>
		</Popover.Target>
	);
}

function LabelPicker() {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const [{ value }, , { setValue }] = useField<graphql.Label>('label');
	const [visible, setVisible] = useState(false);

	const { data } = graphql.usePaginatedLabelsQuery({
		variables: {
			isArchived: false,
			type: graphql.LabelType.Goal,
		},
	});

	const labels = data?.paginatedLabels.edges.map(({ node }) => node) ?? [];
	const options = _.sortBy(
		labels.map(({ id, title, description }) => ({
			value: id,
			title: localize(title).text,
			context: (
				<>
					<Heading mb={2}>{localize(title).text}</Heading>
					{description && <Paragraph mt={2}>{localize(description).text}</Paragraph>}
				</>
			),
		})),
		'title',
	);

	if (options.length === 0) {
		return null;
	}

	const none = t('tags.item.option.none');
	const label = value
		? `${t('goals.show.label.label')}: ${localize(value.title).text}`
		: `${t('goals.show.label.label')}: ${none}`;

	return (
		<Popover.Target
			visible={visible}
			onDismiss={() => setVisible(false)}
			contents={
				<Select.Menu
					options={options}
					selectedValue={value?.id}
					onChange={(id) => {
						if (!data || !id) {
							return;
						}

						setValue(labels.find((item) => item.id === id)!);
						setVisible(false);
					}}
					onClose={() => setVisible(false)}
				/>
			}
		>
			<PropertyChip icon={<TagIcon />} label={label} onClick={() => setVisible(true)} />
		</Popover.Target>
	);
}

function CompetencyPicker() {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const authenticatedUser = useAuthenticatedUser();
	const [visible, setVisible] = useState(false);

	const { data } = graphql.useJobPositionsQuery({
		variables: {
			id: authenticatedUser.id,
			status: graphql.JobPositionStatus.Active,
		},
	});

	const [fetch, query] = graphql.useCompetenciesLazyQuery({
		variables: {
			isArchived: false,
			jobTitleIDs: data?.user.jobPositions.map((jobPosition) => jobPosition.jobTitle.id),
			first: 1000,
		},
	});

	const [{ value }, , { setValue }] = useField<graphql.CompetencyGoal>('competency');

	useEffect(() => {
		if (visible) {
			fetch();
		}
	}, [visible, fetch]);

	const none = t('tags.item.option.none');
	const label = value
		? localize(value.competency.title).text
		: `${t('goals.show.competency.label')}: ${none}`;

	const options = query.data
		? _.sortBy(
				query.data.competencies.edges
					.filter(({ node }) => node.attachableToGoal)
					.map(({ node }) => ({
						value: node.id,
						title: localize(node.title).text,
						context: (
							<>
								<Heading mb={2}>{localize(node.title).text}</Heading>
								<QuillText value={localize(node.description ?? [])?.text} />
							</>
						),
					})),
				'title',
			)
		: undefined;

	return (
		<Popover.Target
			visible={visible}
			onDismiss={() => setVisible(false)}
			contents={
				<Select.Menu
					options={options}
					selectedValue={value?.competency.id}
					onChange={(id) => {
						const competency = query
							.data!.competencies.edges.map(({ node }) => node)
							.find((node) => node.id === id)! as graphql.Competency;
						setValue({ competency, competencyLevelIndex: 0, appraisals: [] });
						setVisible(false);
					}}
					onClose={() => setVisible(false)}
				/>
			}
		>
			<PropertyChip icon={<CompassIcon />} label={label} onClick={() => setVisible(true)} />
		</Popover.Target>
	);
}

function CompetencyLevelPicker() {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const [visible, setVisible] = useState(false);

	const [{ value }, , { setValue }] = useField<graphql.CompetencyGoal>('competency');

	const query = graphql.useCompetencyQuery({
		variables: { id: value?.competency.id },
		skip: !value,
	});

	if (!value) {
		return null;
	}

	const levels = value.competency.levels ?? query.data?.competency.levels;

	if (!levels) {
		return <Tags.Item.Skeleton label={t('goals.show.competencyLevel.label')} />;
	}

	const selected = levels[value.competencyLevelIndex];
	const label = selected
		? localize(selected.level.title).text
		: `${t('goals.show.competencyLevel.label')}: ${t('tags.item.option.none')}`;

	return (
		<Popover.Target
			visible={visible}
			onDismiss={() => setVisible(false)}
			contents={
				<Select.Menu
					options={levels
						.filter(({ enabled }) => enabled)
						.map(({ level, criteria }) => ({
							value: level.id,
							title: localize(level.title).text,
							context: (
								<>
									<Heading>{localize(level.title).text}</Heading>
									<Paragraph mt={2}>
										<QuillText value={localize(criteria ?? [])?.text} />
									</Paragraph>
								</>
							),
						}))}
					selectedValue={selected?.level.id}
					onChange={(id) => {
						const index = levels.findIndex(({ level }) => level.id === id);
						setValue({ competency: value.competency, competencyLevelIndex: index, appraisals: [] });
						setVisible(false);
					}}
					onClose={() => setVisible(false)}
				/>
			}
		>
			<PropertyChip icon={<CompassIcon />} label={label} onClick={() => setVisible(true)} />
		</Popover.Target>
	);
}

function DueDatePicker() {
	const { t } = useTranslation();
	const formik = useFormikContext<{ dueDate?: Date }>();
	const [visible, setVisible] = useState(false);
	const empty = t('datePicker.value.empty');
	const label = formik.values.dueDate
		? moment(formik.values.dueDate).format('LL')
		: `${t('goals.show.dueDate.label')}: ${empty}`;

	return (
		<Popover.Target
			visible={visible}
			onDismiss={() => setVisible(false)}
			contents={
				<DatePicker
					value={formik.values.dueDate}
					onChange={(date) => formik.setFieldValue('dueDate', date)}
					onDismiss={() => setVisible(false)}
				/>
			}
		>
			<PropertyChip icon={<CalendarIcon />} label={label} onClick={() => setVisible(true)} />
		</Popover.Target>
	);
}

const TemplatePickerContainer = styled(Box)`
	border-radius: ${tokens.radius.sm}px;
	background: ${tokens.surface.secondary};
`;

function TemplatePicker(props: { userID: string }) {
	const history = useHistory();
	const templateID = useMemo(() => {
		const search = new URLSearchParams(history.location.search);
		return search.get('template_id');
	}, []);

	const [template, setTemplate] = useState<graphql.GoalTemplate | undefined>(undefined);
	const formik = useFormikContext();
	const { localize } = useLocalize();

	useEffect(() => {
		if (!template) {
			return;
		}

		formik.setFieldValue('title', template.title);
		formik.setFieldValue('description', localize(template.description).text);
		formik.setFieldValue('label', template.label);
		formik.setFieldValue('competency', template.competency as graphql.CompetencyGoal);
		formik.setFieldValue('templateID', template.id);

		if (!_.isNil(template.relativeDueDate)) {
			formik.setFieldValue('dueDate', moment().add(template.relativeDueDate, 'days').toDate());
		}
	}, [template?.id]);

	if (!templateID) {
		return null;
	}

	return (
		<TemplatePickerContainer p={3} mt={2} display="none">
			<GoalTemplateSelector
				userID={props.userID}
				template={template}
				onChange={() => setTemplate(template)}
				onLoad={(templates) => {
					const template = templates.find((template) => template.id === templateID);

					if (!template) {
						return;
					}

					setTemplate(template);
				}}
			/>
		</TemplatePickerContainer>
	);
}

function EditGoal(props: {
	goal?: graphql.Goal;
	userID?: string;
	returnURL: string;
	onReset: () => void;
}) {
	const history = useHistory();
	const { pathname } = history.location;
	const { language, localize } = useLocalize();
	const viewer = useAuthenticatedUser();
	const completeOrganizationGoal = useCompleteWorkspaceCheckpoint('organization.goals');
	const hasAI = useFeature(graphql.FeatureName.Ai);
	const { isModal, close: closeModal } = useContext(ModalContext);
	const params = new URLSearchParams(history.location.search);

	const [create, { loading: loadingCreate }] = graphql.useCreateGoalMutation();
	const [update, { loading: loadingUpdate }] = graphql.useUpdateIndividualGoalMutation();
	const [move, { loading: loadingMove }] = graphql.useMoveGoalMutation();
	const loading = loadingCreate || loadingUpdate || loadingMove;

	const [groups, setGroups] = useState<graphql.Group[]>([]);
	const [parentGoal, setParentGoal] = useState(props.goal?.parent ?? undefined);

	const [saved, setSaved] = useState(false);
	const [descriptionInputRefreshVersion, setDescriptionInputRefreshVersion] = useState(0);

	const initialValues = {
		title: props.goal?.title ?? [{ languageID: language.id, text: '' }],
		description: props.goal?.description ?? { ops: [] },
		momentID: params.get('moment_id') ?? undefined,
		dueDate: props.goal?.dueDate ?? undefined,
		feasibility: props.goal?.feasibility ?? undefined,
		competency: props.goal?.competency ?? undefined,
		subgoals: _.chain(props.goal?.checklistItems ?? [])
			.keyBy('id')
			.value(),
		subgoalIDs: (props.goal?.checklistItems ?? []).map(({ id }) => id),
		label: props.goal?.label ?? undefined,
		groupID:
			props.goal?.owner.__typename === 'Group' ? props.goal?.owner.id : params.get('group_id'),
		workspaceID:
			props.goal?.owner.__typename === 'Workspace'
				? props.goal?.owner.id
				: params.get('workspace_id'),
		visibility: props.goal?.visibility,
		templateID: undefined,
	};

	const formik = useFormik({
		initialValues,
		onSubmit: async (values) => {
			const input: Partial<graphql.CreateGoalInput> = {
				title: values.title,
				description: values.description,
				dueDate: values.dueDate,
				momentID: values.momentID,
				linkedGoalDriverIDs: props.goal?.linkedGoalDrivers?.map(({ id }) => id) ?? [],
				linkedGoalIDs: props.goal?.linkedGoals?.map(({ id }) => id) ?? [],
				feasibility: values.feasibility ?? undefined,
				labelID: values.label?.id ?? undefined,
				competency:
					formik.values.workspaceID || formik.values.groupID
						? undefined
						: values.competency
							? {
									competencyID: values.competency.competency.id,
									competencyLevelIndex: values.competency.competencyLevelIndex,
								}
							: undefined,
				checklistItems: values.subgoalIDs.map((id) => toChecklistItemInput(values.subgoals[id])),
				visibility: values.visibility,
			};

			setSaved(true);

			if (props.goal) {
				await update({ variables: { input: { id: props.goal.id, ...input } as any } });

				if (parentGoal?.id !== props.goal.parent?.id) {
					await move({
						variables: {
							input: {
								goalID: props.goal.id,
								parentID: parentGoal?.id,
							},
						},
					});
				}

				history.push(
					`${props.userID ? `/users/${props.userID}/goals` : `/goals`}/${props.goal.id}`,
				);
			} else {
				const group = groups.find(({ id }) => id === formik.values.groupID);

				const owner = formik.values.workspaceID
					? {
							ownerID: formik.values.workspaceID,
							ownerType: graphql.GoalOwnerType.Workspace,
						}
					: group
						? {
								ownerID: group.id,
								ownerType:
									group.type === graphql.GroupType.Team
										? graphql.GoalOwnerType.Team
										: graphql.GoalOwnerType.Department,
							}
						: {
								ownerID: props.userID ?? viewer.id,
								ownerType: graphql.GoalOwnerType.User,
							};

				const response = await create({
					variables: {
						input: {
							...owner,
							...input,
						} as graphql.CreateGoalInput,
					},
				});

				const goalID = response.data!.createGoal.goal.id;

				if (parentGoal) {
					await move({
						variables: {
							input: {
								goalID,
								parentID: parentGoal.id,
							},
						},
					});
				}

				if (formik.values.workspaceID) {
					completeOrganizationGoal();
				}

				if (
					values.momentID &&
					response.data?.createGoal.goal.moment?.status !== graphql.MomentStatus.Completed
				) {
					history.push(
						props.userID
							? `/users/${props.userID}/goals/new?moment_id=${values.momentID}`
							: `/goals/new?moment_id=${values.momentID}`,
					);
					props.onReset();
				} else {
					if (isModal) {
						closeModal();
					} else {
						history.push(props.userID ? `/users/${props.userID}/goals` : `/goals/${goalID}`);
					}
				}
			}
		},
		enableReinitialize: true,
	});
	const { setFieldValue } = formik;

	const { t } = useTranslation();
	const hasCompetencyGoals = useFeature(graphql.FeatureName.CompetencyGoals);
	const hasTeamGoals = useFeature(graphql.FeatureName.TeamGoals);
	const hasGoalLabels = useFeature(graphql.FeatureName.GoalLabels);
	const containerRef = useRef<HTMLDivElement>(null);
	const { addToast } = useToasts();

	const descriptionInputVersion = useMemo(
		() => `${formik.values.templateID ?? 'scratch'}-${descriptionInputRefreshVersion}`,
		[formik.values.templateID, descriptionInputRefreshVersion],
	);

	const handleOnTakeSuggestion = useCallback(
		async (suggestion: graphql.AiGeneratedGoalSuggestion) => {
			setFieldValue('title', [{ languageID: language.id, text: suggestion.title }]);
			await setFieldValue('description', new QuillDelta().insert(suggestion.description));
			setDescriptionInputRefreshVersion((version) => version + 1);

			const subgoalIDs = suggestion.subgoals.map((subgoal) => {
				const subgoalID = _.uniqueId();

				setFieldValue(`subgoals.${subgoalID}`, {
					title: [{ languageID: language.id, text: subgoal }],
					description: [{ languageID: language.id, text: new QuillDelta() }],
					progress: {
						type: graphql.GoalChecklistItemType.Checkbox,
						value: 0,
						lower: undefined,
						upper: undefined,
					},
					dueDate: undefined,
				});

				return subgoalID;
			});

			setFieldValue('subgoalIDs', subgoalIDs);
			addToast(t('goals.ai.suggestion.inserted.success'), {
				appearance: 'success',
				autoDismiss: true,
			});
		},
		[addToast, language.id, setFieldValue, t],
	);

	const handleAddLearningSubgoal = useCallback(
		(course: graphql.LearningCourseFragment) => {
			const subgoalID = _.uniqueId();

			setFieldValue(`subgoals.${subgoalID}`, {
				title: course.title,
				description: [{ languageID: language.id, text: new QuillDelta() }],
				progress: {
					type: graphql.GoalChecklistItemType.Learning,
					value: 0,
					lower: undefined,
					upper: undefined,
				},
				dueDate: undefined,
				learningCourseID: course.id,
				learningCourse: course,
			});
			setFieldValue('subgoalIDs', [...formik.values.subgoalIDs, subgoalID]);
		},
		[formik.values.subgoalIDs, language.id, setFieldValue],
	);

	// Prefill a subgoal when arriving from the learning library ("Maak leerdoel +").
	const prefillLearningCourseID = params.get('learning_course_id');
	const prefillCourse = graphql.useLearningCourseQuery({
		variables: { id: prefillLearningCourseID! },
		skip: !prefillLearningCourseID || !!props.goal,
	});
	const prefillApplied = useRef(false);

	useEffect(() => {
		const course = prefillCourse.data?.learningCourse;

		if (course && !prefillApplied.current) {
			prefillApplied.current = true;
			handleAddLearningSubgoal(course);
		}
	}, [prefillCourse.data?.learningCourse, handleAddLearningSubgoal]);

	const addedLearningCourseIDs = useMemo(
		() =>
			formik.values.subgoalIDs
				.map((id) => formik.values.subgoals[id]?.learningCourseID)
				.filter((id): id is string => !!id),
		[formik.values.subgoalIDs, formik.values.subgoals],
	);

	const shouldShowPrompt = (nextLocation: Location, action: Action) => {
		const isModal = nextLocation.search.includes('modal');
		const isOnSamePage = nextLocation.pathname.startsWith(pathname);

		if (isOnSamePage || isModal) {
			// Skip the prompt if we are on the same page or are in a modal.
			return true;
		}

		// If the user comes from a non goal page, we don't want to show the prompt.
		// This is needed due to the modal portal keeping this code alive when it is closed.
		const currentLocation = window.location;
		const isCreatingGoal = currentLocation.href.includes('/goals/new');
		const isEditingGoal =
			currentLocation.href.includes('goals') && currentLocation.href.includes('edit');

		if (!isCreatingGoal && !isEditingGoal) {
			return true;
		}

		return t('goals.edit.prompt');
	};

	return (
		<FormikProvider value={formik}>
			<Box py={1}>
				{/* We use the message prop to check if we should show the prompt,
			as the when prop does not have the next location as a parameter. */}
				<Prompt when={formik.dirty && !saved} message={shouldShowPrompt} />
				<Container.Sticky
					py={3}
					header={
						<>
							{formik.values.momentID && <RemainingGoalsStatus momentID={formik.values.momentID} />}

							<Toolbar>
								<ModalContext.Consumer>
									{(value) =>
										!value.isModal && (
											<Button
												icon={<ArrowLeftIcon />}
												link={props.returnURL}
												title={t('goals.edit.toolbar.cancel')}
												type="plain"
												ml={-16}
											/>
										)
									}
								</ModalContext.Consumer>
								<Toolbar.Spacing />
								<Button
									loading={loading}
									title={
										props.goal
											? t('goals.edit.toolbar.save')
											: formik.values.momentID
												? t('goals.edit.toolbar.createAndContinue')
												: t('goals.edit.toolbar.create')
									}
									onClick={() => formik.submitForm()}
								/>
							</Toolbar>
						</>
					}
				>
					<Box mt={2}>
						<Box ref={containerRef}>
							<Heading3 mb={`${tokens.gap.list}px`}>{t('goals.edit.field.title')}</Heading3>
							<FlexGap alignItems="center" gap={tokens.gap.list}>
								<Input
									autoFocus
									value={localize(formik.values.title).text}
									onChange={(text: string) => {
										formik.setFieldValue('title', [{ languageID: language.id, text }]);
									}}
									flexGrow={1}
								/>
								{hasAI && (
									<GoalSuggestionsAI
										initialInput={params.get('prompt') ?? undefined}
										onTakeSuggestion={handleOnTakeSuggestion}
									/>
								)}
							</FlexGap>
							<TemplatePicker userID={props.userID ?? viewer.id} />
						</Box>
						{!props.goal && !formik.values.workspaceID && params.has('group_id') && (
							<Box mt={3}>
								<Label mb={2}>{t('goals.edit.field.group')}</Label>
								<SingleGroupSelector.Formik
									name="groupID"
									canSelect={(group) => group.viewerCanCreateGoal}
									onLoad={setGroups}
								/>
							</Box>
						)}

						<FlexGap
							mt={`${tokens.gap.list}px`}
							flexWrap="wrap"
							alignItems="center"
							gap={tokens.gap.list}
						>
							{props.goal && <FeasibilityPicker />}
							{hasGoalLabels && <LabelPicker />}
							<DueDatePicker />

							{hasTeamGoals && (
								<>
									<GoalSelector
										personalGoals={!formik.values.groupID}
										goalTargetIsUser={!formik.values.groupID && !formik.values.workspaceID}
										goal={parentGoal}
										onChange={(goal, visibility) => {
											setParentGoal(goal);
											if (visibility) {
												formik.setFieldValue('visibility', visibility);
											}
										}}
									/>

									{parentGoal && <VisibilityPicker />}
								</>
							)}

							{!(formik.values.workspaceID || formik.values.groupID) && hasCompetencyGoals && (
								<>
									<CompetencyPicker />
									<CompetencyLevelPicker />
								</>
							)}
						</FlexGap>

						<Box mt={`${tokens.gap.section}px`}>
							<Heading3 mb={2}>{t('goals.edit.field.description')}</Heading3>
							<QuillInput.Formik
								name="description"
								placeholder={t('goals.edit.description.placeholder')}
								// Refresh the editor when a template or AI suggestion changes its value externally.
								key={descriptionInputVersion}
							/>
						</Box>
					</Box>
				</Container.Sticky>
				<GoalLearningSuggestions
					title={localize(formik.values.title).text}
					description={(formik.values.description.ops ?? [])
						.map((op) => (typeof op.insert === 'string' ? op.insert : ''))
						.join('')}
					addedLearningCourseIDs={addedLearningCourseIDs}
					onAdd={handleAddLearningSubgoal}
				/>
				<SubgoalsContainer>
					<Subgoals editable />
				</SubgoalsContainer>
			</Box>
		</FormikProvider>
	);
}

export default function Goal(props: { userID?: string; goalID?: string }) {
	const history = useHistory();
	const params = new URLSearchParams(history.location.search);
	const hasJobGoalTemplates = useFeatureDetails(graphql.FeatureName.JobGoalTemplates);

	const [scratch, setScratch] = useState(false);

	const query = graphql.useIndividualGoalQuery({
		variables: { id: props.goalID! },
		skip: !props.goalID,
	});

	const viewer = useViewer();

	const returnURL = (() => {
		// Editing existing goal
		if (props.goalID) {
			if (props.userID) {
				return `/users/${props.userID}/goals/${props.goalID}`;
			}
			return `/goals/${props.goalID}`;
		}

		// Create team/department goal
		if (params.has('group_id')) {
			return '/goals/collective';
		}

		// Create workspace goal
		if (params.has('workspace_id')) {
			return '/goals/organization';
		}

		// Create personal goal for other user
		if (props.userID) {
			return `/users/${props.userID}/goals`;
		}

		// Create personal goal for viewer
		return '/goals';
	})();

	if (hasJobGoalTemplates.loading) {
		return null;
	}

	if (!props.goalID) {
		if (
			!scratch &&
			!params.has('template_id') &&
			!params.has('workspace_id') &&
			!params.has('group_id') &&
			!params.has('prompt') &&
			!params.has('learning_course_id') &&
			hasJobGoalTemplates.available
		) {
			return (
				<GoalTemplates
					userID={props.userID ?? viewer!.id}
					returnURL={returnURL}
					onScratch={() => setScratch(true)}
				/>
			);
		} else {
			return (
				<EditGoal userID={props.userID} returnURL={returnURL} onReset={() => setScratch(false)} />
			);
		}
	}

	if (query.data) {
		return (
			<EditGoal
				goal={query.data.goal as graphql.Goal}
				userID={props.userID}
				returnURL={returnURL}
				onReset={() => setScratch(false)}
			/>
		);
	} else {
		return null;
	}
}
